/**
 * ExternalUserLinkSection
 * The "Personlig länk" card on an external user's detail (Externa användare):
 * the link's state, last use, the tasks it opens today, the admin actions
 * (copy, change validity, send, rotate, revoke, issue) and the merged log of
 * all the user's links. An
 * expired link is never extended: it offers only a new link (any validity,
 * permanent included) or revoke.
 * Admin only, like the tab it sits in.
 */
import React, { useState } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import { Clock, Link as LinkIcon } from 'lucide-react';
import { useExternalLink } from '../../hooks/useExternalLink';
import { LinkValidity } from '../../api/externalLinksApi';
import { GuestLinkLogEntry, GuestLinkLogType } from '../../types/guestAccess';
import { describeLinkState, isActiveLink, isValidityComplete, suggestLinkValidity, validityOf } from '../../utils/linkValidity';
import { formatDateForDisplay, formatDateTimeForDisplay } from '../../Helpers/DateUtils';
import CopyGuestLinkButton from '../../Components/CopyGuestLinkButton';
import ConfirmationModal from '../../Components/ConfirmationModal';
import LinkValidityControl from '../../Components/LinkValidityControl';
import LinkTaskList from '../../Components/LinkTaskList';

interface ExternalUserLinkSectionProps {
    uid: string;
    getUserName?: (uid?: string) => string;
}

const LOG_LABELS: Record<GuestLinkLogType, string> = {
    issued: 'Länk skapad',
    validityChanged: 'Giltighet ändrad',
    reported: 'Länk spärrad av användaren',
    rotated: 'Ny länk skickad',
    renewed: 'Ny länk (den förra hade gått ut)',
    revoked: 'Länk återkallad',
    linkSent: 'Länk skickad',
    extensionRequested: 'Användaren bad om förlängning',
};

/** "2027-01-10" for dates, the kind in Swedish, anything else as is. */
const describeValue = (value: string): string =>
    value === 'permanent' ? 'permanent' : value === 'expiring' ? 'tidsbegränsad' : /^\d{4}-\d{2}-\d{2}/.test(value) ? formatDateForDisplay(value) : value;

/** Why a link was revoked for good (`revoked` entries carry it in `note`). */
const REVOKE_REASONS: Record<string, string> = {
    manual: 'av förvaltare',
    user_deleted: 'användaren togs bort',
    reported: 'spärrad av användaren',
};

const LogRow: React.FC<{ entry: GuestLinkLogEntry; actorName: (actor: string) => string }> = ({ entry, actorName }) => (
    <div className="d-flex justify-content-between gap-3 py-1 border-bottom small">
        <span>
            {LOG_LABELS[entry.type] ?? entry.type}
            {entry.from && entry.to && <span className="text-muted">{` (${describeValue(entry.from)} → ${describeValue(entry.to)})`}</span>}
            {entry.type === 'issued' && entry.to && <span className="text-muted">{` (${describeValue(entry.to)})`}</span>}
            {entry.type === 'revoked' && entry.note && <span className="text-muted">{` (${REVOKE_REASONS[entry.note] ?? entry.note})`}</span>}
            {entry.type === 'linkSent' && entry.note === 'legacy' && <span className="text-muted"> (från gammal länk)</span>}
            <span className="text-muted">{` · ${actorName(entry.actor)}`}</span>
        </span>
        <span className="text-muted text-nowrap">{formatDateTimeForDisplay(entry.when)}</span>
    </div>
);

type Pending = 'rotate' | 'revoke' | null;
type ValidityMode = 'issue' | 'change' | 'renew';

const VALIDITY_TITLES: Record<ValidityMode, string> = { issue: 'Skapa länk', change: 'Ändra giltighet', renew: 'Skapa ny länk' };
const VALIDITY_SUBMIT: Record<ValidityMode, string> = { issue: 'Skapa', change: 'Spara', renew: 'Skapa och skicka' };

const ExternalUserLinkSection: React.FC<ExternalUserLinkSectionProps> = ({ uid, getUserName }) => {
    const link = useExternalLink(uid);
    const [validityModal, setValidityModal] = useState<{ mode: ValidityMode; value: LinkValidity | null } | null>(null);
    const [pending, setPending] = useState<Pending>(null);
    // Result of the last action that e-mails (or deliberately does not e-mail) the link.
    const [notice, setNotice] = useState<{ variant: 'success' | 'warning'; text: string } | null>(null);
    const emailNotice = (emailSent: boolean, created: boolean) => setNotice(emailSent
        ? { variant: 'success', text: created ? 'En ny länk har skickats till användarens e-post.' : 'Länken har skickats till användarens e-post.' }
        : { variant: 'warning', text: created ? 'Länken är skapad, men e-post kunde inte skickas. Kopiera länken eller försök skicka igen.' : 'E-post kunde inte skickas. Kopiera länken eller försök igen.' });

    const current = link.info?.link ?? null;
    const active = isActiveLink(current);
    const expired = current?.state === 'expired';

    // `actor` is a UID, or the sentinels `system` (self-service flows) and `self` (the worker).
    const actorName = (actor: string) =>
        actor === 'system' ? 'Systemet' : actor === 'self' ? 'Användaren själv' : getUserName?.(actor) || actor;

    // A new link's end date: the latest due date of the user's tasks + 14 days; none → the admin chooses.
    const suggestedValidity = (): LinkValidity | null => {
        const suggestion = suggestLinkValidity(link.info?.tasks ?? []);
        return suggestion ? { kind: 'expiring', expiresAt: suggestion.expiresAt } : null;
    };

    const runQuietly = (action: () => Promise<void>) => action().catch(() => { /* error shown by the hook */ });

    const handleSend = () => runQuietly(async () => {
        setNotice(null);
        const { emailSent } = await link.send();
        emailNotice(emailSent, expired);
    });

    const confirmPending = () => runQuietly(async () => {
        setNotice(null);
        if (pending === 'rotate') emailNotice((await link.rotate()).emailSent, true);
        if (pending === 'revoke') await link.revoke();
        setPending(null);
    });

    const saveValidity = () => {
        if (!validityModal?.value) return;
        const { mode, value } = validityModal;
        void runQuietly(async () => {
            setNotice(null);
            if (mode === 'renew') emailNotice((await link.rotate(value)).emailSent, true);
            else if (mode === 'issue') {
                await link.issue(value);
                setNotice({ variant: 'success', text: 'Länken är skapad. Inget e-postmeddelande har skickats – använd Skicka länk eller kopiera den.' });
            } else await link.setValidity(value);
            setValidityModal(null);
        });
    };

    return (
        <div className="user-detail-card">
            <h6 className="user-detail-card-title">Personlig länk</h6>
            {link.error && <Alert variant="danger" dismissible onClose={link.clearError}>{link.error}</Alert>}
            {notice && <Alert variant={notice.variant} dismissible onClose={() => setNotice(null)}>{notice.text}</Alert>}

            {link.loading && !link.info ? (
                <Spinner animation="border" size="sm" />
            ) : (
                <>
                    <div className="user-activity-grid">
                        <div className="user-activity-item">
                            <div className="user-activity-icon"><LinkIcon size={18} /></div>
                            <div className="user-activity-info">
                                <div className="user-activity-label">Status</div>
                                <div className="user-activity-value">{describeLinkState(current)}</div>
                            </div>
                        </div>
                        <div className="user-activity-item">
                            <div className="user-activity-icon"><Clock size={18} /></div>
                            <div className="user-activity-info">
                                <div className="user-activity-label">Senast använd</div>
                                <div className="user-activity-value">{current?.lastUsedAt ? formatDateTimeForDisplay(current.lastUsedAt) : 'Aldrig'}</div>
                            </div>
                        </div>
                    </div>

                    {link.info && (
                        <div className="mt-3">
                            <LinkTaskList
                                tasks={link.info.tasks}
                                heading={active ? 'Uppgifter via länken' : 'Delegerade uppgifter'}
                                emptyText="Inga öppna uppgifter delegerade."
                            />
                        </div>
                    )}

                    <div className="d-flex flex-wrap gap-2 mt-3">
                        {active ? (
                            <>
                                <CopyGuestLinkButton url={current.url} label="Kopiera länk" className="" />
                                <Button variant="outline-secondary" size="sm" disabled={link.submitting} onClick={() => setValidityModal({ mode: 'change', value: validityOf(current) })}>
                                    Ändra giltighet
                                </Button>
                                <Button variant="outline-secondary" size="sm" disabled={link.submitting} onClick={handleSend}>
                                    Skicka länk
                                </Button>
                                <Button variant="outline-secondary" size="sm" disabled={link.submitting} onClick={() => setPending('rotate')}>
                                    Skicka ny länk
                                </Button>
                                <Button variant="outline-danger" size="sm" disabled={link.submitting} onClick={() => setPending('revoke')}>
                                    Återkalla
                                </Button>
                            </>
                        ) : expired ? (
                            <>
                                <Button variant="outline-primary" size="sm" disabled={link.submitting} onClick={() => setValidityModal({ mode: 'renew', value: suggestedValidity() })}>
                                    Skapa ny länk
                                </Button>
                                <Button variant="outline-danger" size="sm" disabled={link.submitting} onClick={() => setPending('revoke')}>
                                    Återkalla
                                </Button>
                            </>
                        ) : (
                            <Button variant="outline-primary" size="sm" disabled={link.submitting} onClick={() => setValidityModal({ mode: 'issue', value: suggestedValidity() })}>
                                Skapa länk
                            </Button>
                        )}
                    </div>

                    {link.info && link.info.log.length > 0 && (
                        <div className="mt-3">
                            <div className="user-activity-label mb-1">Historik</div>
                            {link.info.log.map((entry) => <LogRow key={entry.key} entry={entry} actorName={actorName} />)}
                        </div>
                    )}
                </>
            )}

            <Modal show={!!validityModal} onHide={() => setValidityModal(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{validityModal && VALIDITY_TITLES[validityModal.mode]}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {validityModal?.mode === 'issue' && (
                    <p className="text-muted small">
                        Länken skapas utan att skickas. Du kan kopiera den eller skicka den till användarens e-post efteråt.
                    </p>
                )}
                {validityModal?.mode === 'renew' && (
                        <p className="text-muted small">
                            Den utgångna länken förlängs inte. En ny länk skapas och skickas till användarens e-post; den gamla slutar gälla.
                        </p>
                    )}
                    {validityModal && (
                        <LinkValidityControl
                            value={validityModal.value}
                            onChange={(value) => setValidityModal({ ...validityModal, value })}
                            disabled={link.submitting}
                            idSuffix="external-user"
                            expiringDefault={suggestedValidity()?.expiresAt ?? (active ? current.expiresAt : undefined)}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setValidityModal(null)} disabled={link.submitting}>Avbryt</Button>
                    <Button variant="primary" onClick={saveValidity} disabled={link.submitting || !validityModal?.value || !isValidityComplete(validityModal.value)}>
                        {link.submitting ? 'Sparar...' : validityModal && VALIDITY_SUBMIT[validityModal.mode]}
                    </Button>
                </Modal.Footer>
            </Modal>

            <ConfirmationModal
                show={pending !== null}
                title={pending === 'rotate' ? 'Skicka ny länk' : 'Återkalla länk'}
                message={pending === 'rotate'
                    ? 'Den nuvarande länken slutar fungera direkt och en ny skickas till användarens e-post. Vill du fortsätta?'
                    : 'Länken slutar fungera direkt och ingen ny skickas. Användaren kan inte längre öppna sina uppgifter. Vill du fortsätta?'}
                confirmText={pending === 'rotate' ? 'Skicka ny länk' : 'Återkalla'}
                variant={pending === 'rotate' ? 'primary' : 'danger'}
                loading={link.submitting}
                onConfirm={() => { void confirmPending(); }}
                onCancel={() => setPending(null)}
            />
        </div>
    );
};

export default ExternalUserLinkSection;
